import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { trackIntegrationEvent } from '@modules/integrationsV2/utils';
import { encodeOAuthState } from '@modules/integrationsV2/utils/encodeOAuthState';
import { LoadingButton } from '@mui/lab';
import { DialogActions, DialogContent, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import querystring from 'querystring';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { getSanitizedUrl } from 'v2/core/utils/sanitizeUrl';

const baseUrl = (subdomain: string) =>
  `https://${subdomain}.zendesk.com/oauth/authorizations/new`;

interface ZendeskSupportFormModalProps {
  integrationType: IntegrationType;
}

export const ZendeskSupportFormModal = ({
  integrationType,
}: ZendeskSupportFormModalProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const { closeModal } = useMuiModalContext();

  const [zendeskSubDomain, setZendeskSubDomain] = useState('');

  const handleTextInputChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setZendeskSubDomain(event.target.value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const queryParams = {
      response_type: 'code',
      client_id: 'zdg-enterpret',
      scope: 'read',
      redirect_uri: `${window.location.origin}/settings/integrations`,
      state: encodeOAuthState({
        channelType: IntegrationChannelType.ZendeskSupport,
        direction: IntegrationDirection.Inbound,
        data: {
          subDomain: zendeskSubDomain,
          tenantSlug,
        },
        integrationType,
      }),
    };

    const redirectZendeskUrl = getSanitizedUrl(
      `${baseUrl(zendeskSubDomain)}?${querystring.stringify(queryParams)}`,
    );

    const windowURL = window.open(redirectZendeskUrl, '_self');
    if (windowURL) {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.ZendeskSupport,
      });
    }
  };

  return (
    <>
      <ModalHeader title="Zendesk Support" showCloseButton={true} />
      <DialogContent dividers>
        <TextField
          fullWidth
          required
          label="Zendesk Sub Domain"
          placeholder="Enter Sub Domain"
          onChange={handleTextInputChange}
          value={zendeskSubDomain}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          variant="contained"
          type="submit"
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={!zendeskSubDomain}
        >
          Connect
        </LoadingButton>
      </DialogActions>
    </>
  );
};
